<template>
  <Modal margin-body="pb2" :width="componentWidth">
    <template #body>
      <div v-if="!loading" class="flex flex-column items-center justify-center">
        <div class="flex col-12" :class="{'flex-column': isMobileView}">
          <div v-if="isReservationAction" class="flex flex-column col-7 items-center justify-center" :class="{'col-12': isMobileView}">
            <h3 class="col-6 my0 center" style="color: #1999D4">Nouveaux participants</h3>
            <CustomSelect
              class="col-8 my2"
              :options="typeOptions"
              :default="typeOptions[0]"
              z-index="z2"
              @input="setType($event)"
            />
            <!-- Club Contacts -->
            <div v-if="isClubAssociatedParticipant" class="flex col-12 justify-center">
              <CustomSelect
                v-if="clubContacts.length"
                :key="`clubContacts_${refreshComponent}`"
                class="col-8 mb2"
                :options="clubContacts"
                :default="clubContacts[0]"
                z-index="z1"
                @input="setParticipant($event)"
              />
              <p v-else class="red center"> Vous n'avez aucune personne associée à votre club qui est disponible pour rejoindre la session. </p>
            </div>
            <!-- Unknown Participant -->
            <div v-else class="flex flex-column col-12 items-center justify-center">
              <div class="flex flex-column col-8 mb2">
                <input v-model="newParticipant.firstname" :class="['border pl2 input-height', {'error-field': errors?.firstname}]" type="text" placeholder="Prénom">
                <span v-if="errors?.firstname" class="red mt1">{{ errors.firstname }}</span>
              </div>

              <div class="flex flex-column col-8 mb2">
                <input v-model="newParticipant.lastname" :class="['border pl2 input-height', {'error-field': errors?.lastname}]" type="text" placeholder="Nom">
                <span v-if="errors?.lastname" class="red mt1">{{ errors.lastname }}</span>
              </div>
            </div>
            <div class="flex mt-auto mt2">
              <Button text="Ajouter un participant" btn-color="blue" text-size="small" :disabled="!participantType" :action="addParticipant" />
            </div>
          </div>

          <div v-if="isReservationAction && !isMobileView" class="divider mx2 col-1"></div>
          <hr v-if="isReservationAction && isMobileView" class="divider my2">

          <div class="flex flex-column col-5 scrollable" :class="{'col-12': (!isReservationAction || isMobileView)}" style="max-height: 300px;">
            <h3 class="my0 center" style="color: #e81d00">Participants inscrits à la session</h3>
            <span v-if="!newParticipants.length" class="mt2 center"> Aucun participant n'est présent à la session. </span>
            <ul class="mt2" style="max-height: 300px;overflow-y: auto;">
              <li v-for="(part, index) in newParticipants" :key="part.uuid" class="mb1">
                <div class="flex align-center">
                  <span class="col-10">{{ part.firstname }} {{ part.lastname }}</span>
                  <i class="col-2 icon icon-cancel h3 pointer black ml2 center" @click="removeParticipant(index)"></i>
                </div>
              </li>
            </ul>
            <div class="flex mt-auto">
              <Button text="Annuler" class="mr1 col-12" btn-color="red" :action="closeModal" />
              <Button text="Confirmateur" class="col-12" btn-color="red" :action="participantsManagement" :disabled="!changesInSession.length" />
            </div>
          </div>
        </div>
      </div>
      <div v-else class="flex flex-wrap center justify-center items-center col-12 border-box z1 px3 py2">
        <div class="loader"></div>
      </div>
      <Transition>
        <Modal v-if="showModal" @closeModal="closeConfirmationModal">
          <template #body>
            <div class="flex items-center container-cart justify-center full-height">
              <div>
                <!-- SUCCESS -->
                <div v-if="successModal" class="mb5 col-12 success-checkmark">
                  <div class="check-icon">
                    <span class="icon-line greener-bg line-tip"></span>
                    <span class="icon-line greener-bg line-long"></span>
                    <div class="icon-circle"></div>
                    <div class="icon-fix"></div>
                  </div>
                </div>
                <div v-else class="mb5 center col-12">
                  <i style="font-size: 88px;" class="icon red line-height-2 icon-not-check-icon"></i>
                </div>
                <div class="h2 center" :class="successModal ? 'green': 'red'">
                  <h2 class="h2n">{{ modalMessage }}</h2>
                </div>
                <div class="mt1 center my2">
                  <Button text="Fermer" class="col-12" btn-color="red" :action="closeConfirmationModal" />
                </div>
              </div>
            </div>
          </template>
        </Modal>
      </Transition>
    </template>
  </Modal>
</template>

<script>
import { v4 as uuidv4 } from 'uuid';
import { onMounted, computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import Modal from '@/components/blocks/Modal';
import Button from '@/components/elements/Button';
import CustomSelect from '@/components/common/CustomSelect';
import { clubPassReservation } from '@/mixins/requests';
import { useViewport } from '@/composables/viewPort.js';

export default {
  components: { Modal, Button, CustomSelect },
  setup (props, context) {
    const store = useStore();
    const { width } = useViewport();
    /* Component funcionality variables */
    const isMobileView = computed(() => width.value <= 832);
    const loading = ref(false);
    const refreshComponent = ref(0);
    /* Participant Management variables */
    const participantType = ref(null);
    const participantToAdd = ref(null);
    const newParticipants = ref([]);
    const participantsInSession = ref([]);
    const newParticipant = ref({
      firstname: '',
      lastname: '',
      uuid: '',
      isClubParticipant: true
    });
    const errors = ref({});
    /* Confirmation Modal variables */
    const showModal = ref(false);
    const successModal = ref(false);
    const modalMessage = ref('');
    const session = computed(() => store.state.session || null);
    const reservations = computed(() => store.state.reservations);
    const isReservationAction = computed(() => session.value.isPassReserve);
    const componentWidth = computed(() => {
      if (isMobileView.value) {
        return '100vw';
      }
      return (isReservationAction.value ? '80vw' : '50vw');
    });
    const isClubAssociatedParticipant = computed(() => participantType.value === 'clubParticipant');
    const changesInSession = computed(() => ([...newParticipants.value.filter(p => !participantsInSession.value.find(part => part.uuid === p.uuid)), ...participantsInSession.value.filter(p => !newParticipants.value.find(part => part.uuid === p.uuid))]));
    /* Club Participant selection */
    const clubContacts = computed(() => (store.state.contacts || []).map(part => {
      const participantIndex = newParticipants.value.findIndex(p => p.uuid === part.uuid);
      if (participantIndex !== -1) {
        return null;
      }
      return { label: `${part.firstname} ${part.lastname}`, firstname: part.firstname, lastname: part.lastname, uuid: part.uuid, isClubParticipant: true };
    }).filter(p => p));
    const typeOptions = ref([
      { label: 'Nouveau participant', value: 'unknown' },
      { label: 'Participant associé', value: 'clubParticipant' }
    ]);
    const setType = typeOfParticipant => {
      participantType.value = typeOfParticipant.value;
    };
    const setParticipant = participant => {
      if (participant.uuid === store.state.user?.uuid) {
        participant.contractOwner = true;
      }
      participantToAdd.value = participant;
    };
    /* Unknown participant */
    const validateParticipant = () => {
      let isValid = true;
      errors.value = {};
      if (!newParticipant.value.firstname) {
        errors.value.firstname = 'Le prénom est obligatoire';
        isValid = false;
      }
      if (!newParticipant.value.lastname) {
        errors.value.lastname = 'Le nom de famille est obligatoire';
        isValid = false;
      }
      return isValid;
    };
    const resetParticipant = () => {
      newParticipant.value = {
        firstname: '',
        lastname: '',
        uuid: '',
        isClubParticipant: true
      };
    };
    /* Common to both types of participants */
    const addParticipant = () => {
      if (isClubAssociatedParticipant.value) {
        refreshComponent.value += 1;
        delete participantToAdd.value.label;
        newParticipants.value.push(participantToAdd.value);
        return;
      }
      if (!validateParticipant()) {
        return;
      }
      newParticipant.value.uuid = `${store.state.user?.uuid}_club_${uuidv4()}`;
      newParticipants.value.push(newParticipant.value);
      resetParticipant();
    };
    const removeParticipant = index => {
      refreshComponent.value += 1;
      newParticipants.value.splice(index, 1);
    };
    const manageReservations = async (participantsToAdd, participantsToRemove) => {
      const args = {
        loggedUser: store.state.user,
        sessionCode: session.value.code,
        passCode: session.value.qrcode.toString(),
        contractNumber: session.value.numeroContrat.toString(),
        participantsToAdd,
        participantsToRemove
      };
      loading.value = true;
      const response = await clubPassReservation(args);
      if (response.success) {
        successModal.value = true;
        modalMessage.value = 'La réservation a été enregistrée avec succès.';
      } else {
        successModal.value = false;
        modalMessage.value = 'Une erreur s\'est produite lors de la réservation. Veuillez réessayer ou contacter le centre.';
      }
      showModal.value = true;
      loading.value = false;
    };
    const participantsManagement = () => {
      const participantsToAdd = newParticipants.value.filter(p => !participantsInSession.value.find(part => part.uuid === p.uuid));
      const participantsToRemove = participantsInSession.value.filter(p => !newParticipants.value.find(part => part.uuid === p.uuid));
      manageReservations(participantsToAdd, participantsToRemove);
    };
    const closeModal = () => {
      context.emit('closeModal');
    };
    const closeConfirmationModal = () => {
      showModal.value = false;
      if (successModal.value) {
        store.commit('setClubSessionUpdated', true);
        closeModal();
      }
    };
    const handleSessionReservationParticipant = () => {
      const participantReservation = [];
      if (session.value && session.value.participants && session.value.participants.length && reservations.value) {
        if (reservations.value && reservations.value.length) {
          reservations.value.map(reservation => {
            reservation.sessions.map(p => {
              (p.participants || []).map(part => {
                if (parseInt(part.contract_number) === parseInt(session.value.numeroContrat) && !participantReservation.some(participant => participant.uuid === part.uuid)) {
                  participantReservation.push({ ...reservation, ...part });
                }
              });
            });
          });
          participantsInSession.value = participantReservation;
          newParticipants.value = participantReservation;
          return;
        }
        participantsInSession.value = session.value.participants.filter(p => parseInt(p.contract_number) === parseInt(session.value.numeroContrat));
        newParticipants.value = session.value.participants.filter(p => parseInt(p.contract_number) === parseInt(session.value.numeroContrat));
      }
    };
    onMounted(() => {
      handleSessionReservationParticipant();
    });
    watch(reservations, () => {
      handleSessionReservationParticipant();
    });
    return { addParticipant, typeOptions, setType, participantType, isClubAssociatedParticipant, clubContacts, closeModal, newParticipants, removeParticipant, participantToAdd, setParticipant, refreshComponent, newParticipant, errors, participantsManagement, isReservationAction, changesInSession, loading, showModal, successModal, modalMessage, closeConfirmationModal, isMobileView, componentWidth };
  }
};
</script>
<style scoped>
  .error-field{
    border-color: red;
  }
  .divider {
    width: 1px;
    background-color: #ccc;
  }
</style>
